import { List, Map } from 'immutable';
import { createSelector } from 'reselect';

export const getRoot = state => state.get('app') || state;

// =============================================================================
// environment
// =============================================================================
const getEnv = state => getRoot(state).get('env');

export const getIsEnvProduction = state => getEnv(state).get('op') === 'prodnew';

export const getIsEnvDevelopment = state => getEnv(state).get('node') === 'development';

export const getIsEnvTest = state => getEnv(state).get('node') === 'test' || process.env.NODE_ENV === 'test';

// =============================================================================
// feature flags
// =============================================================================
export const getFeatureFlags = state => getRoot(state).get('feature_flags');

// =============================================================================
// Search
// =============================================================================
export const getSearch = state => getRoot(state).get('search');

export const getResults = state => getSearch(state).get('results');

export const showResults = state => getResults(state) !== null;

export const getNumResults = createSelector(getResults, results => (results ? results.size : 0));

// Sort
export const getIsSortReverse = state => getSearch(state).get('isSortReverse');

export const getSortByField = state => getSearch(state).get('sortByField');

const getComparatorFn = createSelector(
    getIsSortReverse,
    isSortReverse => (isSortReverse
        ? (a, b) => {
            if (a > b) { return -1; }
            if (a < b) { return 1; }
            if (a === b) { return 0; }
        }
        : (a, b) => {
            if (a < b) { return -1; }
            if (a > b) { return 1; }
            if (a === b) { return 0; }
        }),
);

const getComparatorValueMapper = createSelector(
    getSortByField,
    field => (item) => {
        if (typeof item.get(field) === 'string') {
            return item.get(field).toLowerCase();
        }
        return item.get(field);
    },
);

export const getSortedResults = createSelector(
    [getResults, getComparatorValueMapper, getComparatorFn],
    (results, comparatorValueMapper, comparator) => {
        if (!results) {
            return List();
        }

        return results.sortBy(comparatorValueMapper, comparator);
    },
);

// Paginate
export const getNumPerPage = state => getSearch(state).get('perPage');

export const getLoadingSearch = state => getSearch(state).get('isLoading');

export const getAffiliateSelector = state => getRoot(state).get('affiliate_selector');
export const isAffiliateSelectorOpen = state => getAffiliateSelector(state).get('isOpen');
export const getSelectedAffiliates = state => getAffiliateSelector(state).get('selectedItems');
export const getConfirmedAffiliates = state => getAffiliateSelector(state).get('confirmedItems');

export const getNumPages = createSelector(
    [getResults, getNumPerPage],
    (results, perPage) => (results ? Math.ceil(results.size / perPage) : 0),
);

export const getPaginatedResults = createSelector(
    [getSortedResults, getNumPerPage, getNumPages],
    (sortedResults, perPage, numPages) => {
        let paginated = List();

        for (let i = 0; i < numPages; i++) {
            const start = i * perPage;
            const page = sortedResults.slice(start, (start + perPage));
            paginated = paginated.push(page);
        }

        return paginated;
    },
);

export const getActivePage = state => getSearch(state).get('activePage');

export const getActivePageData = createSelector([
    getActivePage,
    getPaginatedResults,
], (activePage, paginated) => paginated.get(activePage - 1) || []);

// =============================================================================
// Types
// =============================================================================

const getTypes = state => getRoot(state).get('types');

const getOrganizatioTypes = state => getTypes(state).get('organization');

export const getOrganizatioTypesByID = createSelector(
    getOrganizatioTypes,
    types => types.reduce((prev, curr) => prev.set(curr.get('id'), curr.get('name')), Map()),
);

export const getOrganizatioTypesByItem = createSelector(
    getOrganizatioTypes,
    types => types.map(value => Map({
        key: value.get('id'),
        value: value.get('id'),
        text: value.get('name'),
    })),
);

